import { useCallback, useMemo, useState } from "react"
import {
  buildFinancialChartLayout,
  CHART_LEFT_PADDING,
  CHART_RIGHT_PADDING,
  overlayStartX,
  priceFromClientY,
} from "@/lib/chart/render"
import type { ChartOverlay, ChartToolMode } from "@/types/chartOverlays"
import type { FuturesBar, FuturesChartPreferences } from "@/types/futuresChart"

const HIT_TOLERANCE = 8

interface UseChartDrawingOptions {
  bars: FuturesBar[]
  preferences: FuturesChartPreferences
  viewport: { start: number; end: number }
  size: { width: number; height: number }
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  overlays: ChartOverlay[]
  toolMode: ChartToolMode
  enabled: boolean
  onPlaceDrawing: (price: number, originTime: string | null) => void
  onMoveDrawing: (overlayId: string, price: number) => void
  onSelectDrawing: (overlayId: string | null) => void
}

export function useChartDrawing({
  bars,
  preferences,
  viewport,
  size,
  canvasRef,
  overlays,
  toolMode,
  enabled,
  onPlaceDrawing,
  onMoveDrawing,
  onSelectDrawing,
}: UseChartDrawingOptions) {
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null)
  const [draggingOverlayId, setDraggingOverlayId] = useState<string | null>(null)
  // While dragging, the level moves locally; the new price is saved once on pointer up.
  const [dragPreview, setDragPreview] = useState<{ id: string; price: number } | null>(null)

  const layout = useMemo(
    () =>
      buildFinancialChartLayout({
        width: size.width,
        height: size.height,
        bars,
        start: viewport.start,
        end: viewport.end,
        preferences,
      }),
    [bars, preferences, size.height, size.width, viewport.end, viewport.start],
  )

  const overlayAtPointer = useCallback(
    (clientX: number, clientY: number): ChartOverlay | null => {
      const canvas = canvasRef.current
      if (!canvas || !layout) return null
      const price = priceFromClientY(clientY, canvas, layout)
      if (price === null) return null

      const bounds = canvas.getBoundingClientRect()
      const pointerX = clientX - bounds.left
      const pointerY = clientY - bounds.top
      let closest: ChartOverlay | null = null
      let closestDistance = Number.POSITIVE_INFINITY
      overlays.forEach((overlay) => {
        const y = layout.priceScale.map(overlay.price)
        // Off-range levels are drawn as edge markers, not lines, so they can't be grabbed.
        if (y < layout.main.top || y > layout.main.bottom) return
        if (pointerX < overlayStartX(overlay, layout, bars, viewport.start) - HIT_TOLERANCE) return
        const distance = Math.abs(pointerY - y)
        if (distance <= HIT_TOLERANCE && distance < closestDistance) {
          closest = overlay
          closestDistance = distance
        }
      })
      return closest
    },
    [bars, canvasRef, layout, overlays, viewport.start],
  )

  const handleDrawingPointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): boolean => {
      if (!enabled || !layout) return false
      const canvas = canvasRef.current
      if (!canvas) return false

      if (toolMode === "pan") {
        const hit = overlayAtPointer(event.clientX, event.clientY)
        if (hit && !hit.id.startsWith("alert-")) {
          setSelectedOverlayId(hit.id)
          onSelectDrawing(hit.id)
          setDraggingOverlayId(hit.id)
          event.currentTarget.setPointerCapture(event.pointerId)
          return true
        }
        setSelectedOverlayId(null)
        onSelectDrawing(null)
        return false
      }

      const price = priceFromClientY(event.clientY, canvas, layout)
      if (price === null || price <= 0) return true

      const hoverIndex = barIndexFromPointer(event.clientX, canvas, viewport)
      const originTime =
        toolMode === "horizontal_ray" && hoverIndex !== null ? (bars[hoverIndex]?.time ?? null) : null
      onPlaceDrawing(price, originTime)
      return true
    },
    [
      bars,
      canvasRef,
      enabled,
      layout,
      onPlaceDrawing,
      onSelectDrawing,
      overlayAtPointer,
      toolMode,
      viewport,
    ],
  )

  const handleDrawingPointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): boolean => {
      if (!enabled || !layout || !draggingOverlayId) return false
      const canvas = canvasRef.current
      if (!canvas) return false
      const price = priceFromClientY(event.clientY, canvas, layout)
      if (price === null || price <= 0) return true
      setDragPreview({ id: draggingOverlayId, price })
      return true
    },
    [canvasRef, draggingOverlayId, enabled, layout],
  )

  const handleDrawingPointerUp = useCallback((): void => {
    if (dragPreview) onMoveDrawing(dragPreview.id, dragPreview.price)
    setDragPreview(null)
    setDraggingOverlayId(null)
  }, [dragPreview, onMoveDrawing])

  return {
    dragPreview,
    selectedOverlayId,
    setSelectedOverlayId,
    handleDrawingPointerDown,
    handleDrawingPointerMove,
    handleDrawingPointerUp,
  }
}

function barIndexFromPointer(
  clientX: number,
  canvas: HTMLCanvasElement,
  viewport: { start: number; end: number },
): number | null {
  const bounds = canvas.getBoundingClientRect()
  const plotX = clientX - bounds.left - CHART_LEFT_PADDING
  const plotWidth = bounds.width - CHART_LEFT_PADDING - CHART_RIGHT_PADDING
  if (plotX < 0 || plotX > plotWidth || viewport.end <= viewport.start) return null
  const local = Math.min(
    viewport.end - viewport.start - 1,
    Math.max(0, Math.floor((plotX / plotWidth) * (viewport.end - viewport.start))),
  )
  return viewport.start + local
}
